import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {
  AddDeviceMarkerSuccess,
  FloorPlanEditorDeviceMarkerActionTypes,
  RemoveAllDeviceMarkerSuccess,
  RemoveDeviceMarker,
  RemoveDeviceMarkerSuccess,
  SetDeviceMarker,
  SetInitialDeviceMarkers,
  SetInitialDeviceMarkersSuccess,
  UpdateDeviceMarkerSuccess
} from '../actions/floor-plan-editor-device-marker.actions';
import {FloorPlanDevice} from '../../afb-api/models/floor';
import {map, withLatestFrom} from 'rxjs/operators';
import {DeviceMarker, RemoveDeviceMarkerRequest} from '../../floor-plan-shared/models/device-marker';
import {Store} from '@ngrx/store';
import * as fromFloorPlanEditor from '../reducers';
import {UtilService} from '../../core';
import {State} from '../reducers/floor-plan-editor-device-marker.reducer';

@Injectable()
export class FloorPlanEditorDeviceMarkerEffects {

  @Effect()
  setInitialDeviceMarkers$ = this.actions$.pipe(
    ofType(FloorPlanEditorDeviceMarkerActionTypes.SetInitialDeviceMarkers),
    map((action: SetInitialDeviceMarkers) => action.payload),
    map((floorPlanDevices: FloorPlanDevice[]) => {
      const entities = floorPlanDevices.reduce((acc: { [deviceUuid: string]: DeviceMarker }, deviceMarker: DeviceMarker) => {
        return {
          ...acc,
          [deviceMarker.deviceUuid]: deviceMarker
        };
      }, {});
      const deviceUuids: string[] = floorPlanDevices.map((floorPlanDevice: FloorPlanDevice) => floorPlanDevice.deviceUuid);

      return new SetInitialDeviceMarkersSuccess({
        entities,
        deviceUuids
      });
    })
  );

  @Effect()
  setDeviceMarker$ = this.actions$.pipe(
    ofType(FloorPlanEditorDeviceMarkerActionTypes.SetDeviceMarker),
    map((action: SetDeviceMarker) => action.payload),
    withLatestFrom(this.floorPlanEditorState$.select(fromFloorPlanEditor.floorPlanEditorDeviceMarkerState)),
    map(([deviceMarker, state]: [DeviceMarker, State]) => {
      const newEntities = {
        ...state.entities,
        [deviceMarker.deviceUuid]: {
          deviceUuid: deviceMarker.deviceUuid,
          name: deviceMarker.name,
          x: deviceMarker.x,
          y: deviceMarker.y
        }
      };
      const payload = {
        entities: newEntities,
        deviceUuid: deviceMarker.deviceUuid,
        didEntitiesChange: !this.utilService.deepEquals(
          this.removePropertyFromDeviceMarker(state.initialEntities, 'name'),
          this.removePropertyFromDeviceMarker(newEntities, 'name')
        )
      };

      if (state.entities[deviceMarker.deviceUuid] != null) {
        return new UpdateDeviceMarkerSuccess(payload);
      } else {
        return new AddDeviceMarkerSuccess(payload);
      }
    })
  );

  @Effect()
  removeDeviceMarker$ = this.actions$.pipe(
    ofType(FloorPlanEditorDeviceMarkerActionTypes.RemoveDeviceMarker),
    map((action: RemoveDeviceMarker) => action.payload),
    withLatestFrom(this.floorPlanEditorState$.select(fromFloorPlanEditor.floorPlanEditorDeviceMarkerState)),
    map(([{deviceUuid}, state]: [RemoveDeviceMarkerRequest, State]) => {
      const {[deviceUuid]: deletedValue, ...newEntities} = state.entities;

      return new RemoveDeviceMarkerSuccess({
        deviceUuid,
        entities: newEntities,
        didEntitiesChange: !this.utilService.deepEquals(
          this.removePropertyFromDeviceMarker(state.initialEntities, 'name'),
          this.removePropertyFromDeviceMarker(newEntities, 'name')
        )
      });
    })
  );

  @Effect()
  removeAllDeviceMarker$ = this.actions$.pipe(
    ofType(FloorPlanEditorDeviceMarkerActionTypes.RemoveAllDeviceMarker),
    withLatestFrom(this.floorPlanEditorState$.select(fromFloorPlanEditor.deviceMarkerUuids)),
    map(([action, deviceMarkerUuids]: [RemoveDeviceMarker, string[]]) => {
      return new RemoveAllDeviceMarkerSuccess({
        deviceMarkerUuids
      });
    })
  );

  constructor(private actions$: Actions,
              private floorPlanEditorState$: Store<fromFloorPlanEditor.State>,
              private utilService: UtilService) {
  }

  private removePropertyFromDeviceMarker(entities: { [deviceUuid: string]: DeviceMarker }, propertyName: string) {
    if (Object.keys(entities).length > 0) {
      return Object.keys(entities).reduce((acc: { [deviceUuid: string]: any }, key: string) => {
        const {[propertyName]: deletedValue, ...newDeviceMarker} = entities[key];
        return {
          ...acc,
          [key]: newDeviceMarker
        };
      }, {});
    } else {
      return entities;
    }
  }
}
